/**
 * Shared processo-judicial hook used by AutosPage and ProcessesPage.
 *
 * Centralizes:
 * - persist meta filter: 202 with `data: null` does not persist (offline
 *   reload would otherwise be stuck on the loading screen forever).
 * - 304: reuses last snapshot via the React Query cache to avoid UI flicker.
 * - 202: surfaces `accepted=true` plus optional polling (`pollOn202`).
 * - 403 with a *definitive* denial code: calls `oracle.recordDenied` (oracle
 *   does removeQueries + notifies subscribers; hook re-renders without
 *   `data`). Transient codes (e.g. ACESSO_VALIDACAO_INDISPONIVEL) are left
 *   as a plain `error` so the query stays retryable — see `accessMessages`.
 *
 * Access state comes from `AccessOracle` via `useAccessState`
 * (no tearing thanks to `useSyncExternalStore`). The oracle is optimistic:
 * the query fires unless access is *known* denied. The backend gate
 * (`validar_acesso`) is the source of truth — it populates the oracle via
 * the success / 403 effects below. Gating the query on `=== 'allowed'`
 * would deadlock: the query is what produces the `allowed` decision.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';

import { fetchProcessoJudicial, type ProcessoJudicialResponse } from '../api/processual';
import { isDefinitiveDenial } from '../lib/access/accessMessages';
import { useAccessOracle, useAccessState } from '../lib/access/accessOracle';
import { classifyError } from '../lib/errors/classifyError';
import { queryKeys, queryTimings } from '../lib/pwa/queryConfig';
import { queryPersistMeta } from '../lib/pwa/queryPersistence';
import { useChaveDeAcesso } from './useDelegacoes';

const POLL_INITIAL_FETCH_MS = 5_000;
const POLL_INITIAL_FETCH_MAX_MS = 60_000;

export interface UseProcessoJudicialOptions {
  enabled?: boolean;
  /**
   * Short polling for 202 (initial load). Default false. Used ONLY by
   * ProcessesPage (point-in-time search without SSE). AutosPage does not
   * use it — banner "Reconectando" + manual button cover the gap.
   */
  pollOn202?: boolean;
}

export interface UseProcessoJudicialResult {
  data: ProcessoJudicialResponse | null;
  accepted: boolean;
  jobId: string | null;
  modo: string | null;
  isLoading: boolean;
  error: unknown;
  /**
   * Non-null when the oracle knows access is *definitively* denied (memo IDB,
   * query 403 with a definitive code, or SSE revocation). The oracle clears
   * `data` and `removeQueries` wipes `error` — callers must read this flag,
   * not `error`, to reliably render the access error state. Transient
   * failures (ACESSO_VALIDACAO_INDISPONIVEL) stay in `error`, not here.
   */
  accessDenied: { codigo: string; tipoAcesso: string; motivo: string | null } | null;
  /**
   * True quando o polling de 202 (`pollOn202`) esgotou a janela sem o preparo
   * terminar. O chamador deve sair do estado "consultando" e oferecer nova
   * tentativa via `retryInitialLoad`. Sempre false sem `pollOn202`.
   */
  pollExpired: boolean;
  /** Reinicia a janela de polling de 202 e refaz a consulta. */
  retryInitialLoad: () => void;
  refetch: () => void;
  /** Quando a tela recebeu a consulta (epoch ms); 0 sem dado. Do cache offline, a da última vez. */
  consultadoEm: number;
}

interface QueryShape {
  data: ProcessoJudicialResponse | null;
  accepted: boolean;
  jobId: string | null;
  modo: string | null;
  /** Last-Modified RFC 7231 propagado pelo backend para round-trips condicionais. */
  lastModified: string | null;
}

export function useProcessoJudicial(
  numero: string,
  options: UseProcessoJudicialOptions = {},
): UseProcessoJudicialResult {
  const oracle = useAccessOracle();
  const queryClient = useQueryClient();
  // CPF próprio, ou o titular no contexto delegado (autos com o certificado dele).
  const cpf = useChaveDeAcesso(numero);

  // Último snapshot bom — em STATE (sem ref). Inicializado a partir do cache
  // rehidratado (IDB) na primeira render para esse processo, para que um 202
  // imediato preserve o snapshot que já estava em disco. O queryFn lê o
  // último snapshot direto do React Query cache via `queryClient.getQueryData`
  // — sempre fresco, sem closure stale, sem ler refs durante render.
  const initialSnapshot = numero
    ? ((queryClient.getQueryData(queryKeys.processual.consulta(numero)) as
        | QueryShape
        | undefined)?.data ?? null)
    : null;
  const [lastSnapshot, setLastSnapshot] =
    useState<ProcessoJudicialResponse | null>(initialSnapshot);

  // Janela de polling do 202 (só com pollOn202). `pollExpired` marca que a
  // janela esgotou sem dados; `pollEpoch` reabre a janela no retry (o effect
  // de polling depende dele — sem o bump, `accepted` continuaria true e o
  // effect nunca re-executaria).
  const [pollExpired, setPollExpired] = useState(false);
  const [pollEpoch, setPollEpoch] = useState(0);

  // Hydrate oracle from IDB-persisted decision. Idempotent.
  useEffect(() => {
    if (!numero || !cpf) return;
    void oracle.hydrateFromMemo(numero, cpf);
  }, [numero, cpf, oracle]);

  // Reseta o snapshot entre processos diferentes — sem isso o snapshot do
  // processo anterior poderia vazar num 202/304 quando o parâmetro de rota
  // muda. AutosPage normalmente desmonta entre processos, mas mudanças de
  // rota podem trocar o caminho sem garantir unmount. Re-hidrata do cache
  // do queryClient (IDB persistido) para o novo processo. Sincronização
  // durante render via comparação prévia + setState — padrão idiomático
  // para reagir a mudança de prop sem useEffect.
  const [trackedNumero, setTrackedNumero] = useState(numero);
  if (trackedNumero !== numero) {
    const cached = numero
      ? ((queryClient.getQueryData(queryKeys.processual.consulta(numero)) as
          | QueryShape
          | undefined)?.data ?? null)
      : null;
    setTrackedNumero(numero);
    setLastSnapshot(cached);
    setPollExpired(false);
  }

  const accessState = useAccessState(numero, cpf);

  const query = useQuery<QueryShape>({
    queryKey: queryKeys.processual.consulta(numero),
    queryFn: async () => {
      // Leitura do cache do React Query — fora do render, sempre fresca.
      const previous = queryClient.getQueryData(
        queryKeys.processual.consulta(numero),
      ) as QueryShape | undefined;
      const previousData = previous?.data ?? null;
      const previousLastModified = previous?.lastModified ?? null;

      const result = await fetchProcessoJudicial(numero, {
        ifModifiedSince: previousLastModified,
      });

      if (result.notModified) {
        // 304 — reuse last snapshot rather than emit `data: null`. Mantém o
        // Last-Modified para o próximo round-trip condicional.
        return {
          data: previousData,
          accepted: false,
          jobId: null,
          modo: null,
          lastModified: previousLastModified,
        };
      }
      if (result.accepted) {
        // 202 — stale-while-revalidate: preserva o último snapshot bom em
        // vez de derrubar a UI para "preparing". O `accepted=true` separado
        // sinaliza para a AutosFreshnessStrip mostrar "atualizando do CNJ".
        // Se nunca tivemos snapshot (cold-start verdadeiro), devolve null e
        // o front cai no estado "preparing" como antes. AccessOracle continua
        // sendo o único bloqueio real — falha transitória do upstream não
        // tira o usuário do ar (princípio do PLANO_ACESSO §8: último estado
        // conhecido vale, salvo revogação explícita).
        return {
          data: previousData,
          accepted: true,
          jobId: result.jobId,
          modo: result.modo,
          lastModified: previousLastModified,
        };
      }
      return {
        data: result.data,
        accepted: false,
        jobId: result.jobId,
        modo: result.modo,
        lastModified: result.lastModified,
      };
    },
    // Optimistic: fire unless the oracle *knows* access is denied. A 403
    // from the backend populates the oracle and then disables the refetch.
    enabled: !!numero && !!cpf && accessState.kind !== 'denied' && (options.enabled ?? true),
    meta: queryPersistMeta.when((r: unknown) => !!(r as QueryShape | undefined)?.data),
    ...queryTimings.processual,
  });

  // Sincroniza `lastSnapshot` com o `query.data` corrente — pega o caso da
  // rehidratação do IDB e o caso de cada fetch novo. Bail-out por comparação
  // de referência: setState com mesmo valor é no-op no React.
  if (query.data?.data && lastSnapshot !== query.data.data) {
    setLastSnapshot(query.data.data);
  }

  // Success: register permission in oracle (single write: memory + IDB + notify)
  useEffect(() => {
    if (query.isSuccess && query.data?.data && cpf) {
      const cacheInfo = query.data.data._cache;
      void oracle.recordAllowed(
        numero,
        cpf,
        cacheInfo.tipo_acesso ?? 'INDETERMINADO',
        'live',
      );
    }
  }, [query.isSuccess, query.data, cpf, oracle, numero]);

  // 403 with a *definitive* denial code: register denial (oracle does
  // removeQueries + notify). Transient codes (ACESSO_VALIDACAO_INDISPONIVEL)
  // are intentionally NOT persisted: they stay as `query.error`, keeping the
  // query enabled, which lets "Tentar novamente" actually refetch.
  useEffect(() => {
    if (!query.error || !cpf) return;
    const cls = classifyError(query.error);
    if (cls.status === 403 && isDefinitiveDenial(cls.codigo)) {
      void oracle.recordDenied(
        numero,
        cpf,
        cls.codigo,
        cls.tipoAcesso ?? 'INDETERMINADO',
        cls.detail,
        'live',
      );
    }
  }, [query.error, cpf, oracle, numero]);

  // Polling 202 — opt-in via pollOn202 (ProcessesPage). AutosPage does NOT use it.
  // Dados chegaram (accepted caiu): a janela de preparo acabou — reset via
  // sincronização durante o render, mesmo padrão do trackedNumero acima.
  if (pollExpired && !query.data?.accepted) {
    setPollExpired(false);
  }

  useEffect(() => {
    if (!query.data?.accepted) return;
    if (!options.pollOn202) return;
    const start = Date.now();
    const id = setInterval(() => {
      if (Date.now() - start > POLL_INITIAL_FETCH_MAX_MS) {
        clearInterval(id);
        setPollExpired(true);
        return;
      }
      queryClient.invalidateQueries({
        queryKey: queryKeys.processual.consulta(numero),
      });
    }, POLL_INITIAL_FETCH_MS);
    return () => clearInterval(id);
  }, [query.data?.accepted, options.pollOn202, numero, queryClient, pollEpoch]);

  const retryInitialLoad = useCallback(() => {
    setPollExpired(false);
    setPollEpoch((epoch) => epoch + 1);
    void queryClient.invalidateQueries({
      queryKey: queryKeys.processual.consulta(numero),
    });
  }, [numero, queryClient]);

  // Suppress data + flow flags when oracle says denied
  const accessDenied =
    accessState.kind === 'denied'
      ? {
          codigo: accessState.codigo,
          tipoAcesso: accessState.tipoAcesso,
          motivo: accessState.motivo,
        }
      : null;
  const denied = accessDenied !== null;
  // Sticky: cai no último snapshot bom mesmo quando query.data zera por algum
  // motivo (refetch transitório, hidratação incompleta). O gate `denied`
  // continua sendo a única autoridade para suprimir o conteúdo.
  return {
    data: denied ? null : (query.data?.data ?? lastSnapshot),
    accepted: denied ? false : (query.data?.accepted ?? false),
    jobId: denied ? null : (query.data?.jobId ?? null),
    modo: denied ? null : (query.data?.modo ?? null),
    isLoading: query.isLoading,
    error: query.error,
    accessDenied,
    pollExpired: denied ? false : pollExpired,
    retryInitialLoad,
    refetch: query.refetch,
    consultadoEm: query.dataUpdatedAt,
  };
}
